// Copyright (c) Docugami, Inc. All rights reserved.

import { AppState } from '../../../store';
import { SettingsPanelOpenedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { FeatureFlags } from 'store/featureFlags/types';
import { FeatureFlagToggle } from 'components/feature_flags/FeatureFlagToggle';
import { Panel, PrimaryButton, Separator, Dropdown, IDropdownOption } from "@fluentui/react";
import { getNumberOfChunkSuggestionsForDemoMode, setNumberOfChunkSuggestionsForDemoMode } from '../../../utils/localStorageHelpers';
import { setSettingsPanelShowing } from '../../../store/settings/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import 'components/settings/css/SettingsPanel.scss'
import { useFeatureFlag } from 'hooks/FeatureFlags';
import { TelemetryContextPanel } from 'telemetry/TelemetryContextPanel';
import { useAppTheme, ThemeId } from 'theming';

export const internalSettingsEnabled: boolean = process.env.REACT_APP_ENV === "staging" || process.env.REACT_APP_ENV === "dev";

export const SettingsPanel = (): JSX.Element => {
    const show = useSelector((state: AppState) => state.settings.settingsPanelShowing);
    const dispatch = useDispatch();

    useEffect(() => {
        if (show) {
            Telemetry.trackEvent<SettingsPanelOpenedSchema>({
                eventName: TelemetryEvent.SettingsPanelOpened
            });
        }
    }, [show]);

    function closePanel(): void {
        dispatch(setSettingsPanelShowing(false));
    }

    return (
        <Panel
            isOpen={show}
            isBlocking={false}
            isLightDismiss={true}
            isFooterAtBottom={true}
            onDismiss={closePanel}
            onOuterClick={closePanel}
            onLightDismissClick={closePanel}
            styles={{ main: { height: "100%", inset: "0px 50px", boxShadow: "rgba(0, 0, 0, 0.12) 5px 2px 15px 0px" } }}
            headerText="Settings">
            <WebappSettings />
        </Panel>
    )
}

const WebappSettings = (): JSX.Element => {
    const diagnosticLoggingEnabled = useFeatureFlag(FeatureFlags.DiagnosticLoggingEnabled);

    return (
        <>
            <ThemeSettings />
            <DebugSettings />
            <FeatureFlagSettings />
            {diagnosticLoggingEnabled && <TelemetryContextPanel />}
        </>
    );
}

const ThemeSettings = (): JSX.Element => {
    const { theme, themeId, setTheme, availableThemes, getTheme } = useAppTheme();

    const themeOptions: IDropdownOption[] = availableThemes.map(id => {
        const themeConfig = getTheme(id);
        return {
            key: id,
            text: themeConfig?.name || id,
        };
    });

    const handleThemeChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            setTheme(option.key as ThemeId);
        }
    };

    return (
        <div className="settingsCollection">
            <Separator className="settingsSeparator">Appearance</Separator>
            <Dropdown
                label="Application Theme"
                selectedKey={themeId}
                options={themeOptions}
                onChange={handleThemeChange}
                styles={{ dropdown: { width: 200 } }}
            />
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '10px',
                padding: '8px',
                background: '#f5f5f5',
                borderRadius: '4px'
            }}>
                <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    background: theme.colors.primaryDark,
                    border: '1px solid rgba(0,0,0,0.1)'
                }} />
                <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    background: theme.colors.primary,
                    border: '1px solid rgba(0,0,0,0.1)'
                }} />
                <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    background: theme.colors.primaryLight,
                    border: '1px solid rgba(0,0,0,0.1)'
                }} />
                <span style={{ fontSize: '13px', color: '#666' }}>{theme.brand.productName}</span>
            </div>
        </div>
    );
}

const DebugSettings = (): JSX.Element => {
    function testCrashTelemetry(): void {
        throw new Error('Test Crash: Nothing to worry about');
    }

    return (
        <div className="settingsCollection">
            <Separator className="settingsSeparator">Debug</Separator>
            <PrimaryButton
                key="TestCrashTelemetryButton"
                onClick={testCrashTelemetry}>Test Crash Telemetry</PrimaryButton>
        </div>
    )
}

const NumChunkSuggestionsDropDown = (): JSX.Element => {
    return <Dropdown
        label={"Max Chunk Suggestions"}
        multiSelect={false}
        options={[10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 1000].map(x => { return { text: x.toString(), key: x } })}
        defaultSelectedKey={getNumberOfChunkSuggestionsForDemoMode()}
        onChange={(ev, option) => { if (option?.key) setNumberOfChunkSuggestionsForDemoMode(option.key as number) }} />
}

const FeatureFlagSettings = (): JSX.Element => {
    const demoModeEnabled = useFeatureFlag(FeatureFlags.DemoFeatures);

    return (
        <div className="settingsCollection">
            <Separator className="settingsSeparator">Feature Flags</Separator>
            <FeatureFlagToggle featureFlag={FeatureFlags.DiagnosticLoggingEnabled} label="Diagnostic Logging" />
            <FeatureFlagToggle featureFlag={FeatureFlags.DownloadAllPipelineOutput} label="Download all Pipeline Output" />
            <FeatureFlagToggle featureFlag={FeatureFlags.DemoFeatures} label="Demo Features" />
            {demoModeEnabled ? <NumChunkSuggestionsDropDown /> : null}
            <FeatureFlagToggle featureFlag={FeatureFlags.ChunkSourceDebugEnabled} label="Chunk Source Debug" />
            <FeatureFlagToggle featureFlag={FeatureFlags.DebugTokensEnabled} label="Tokens Debug" />
            <FeatureFlagToggle featureFlag={FeatureFlags.SdvDebugEnabled} label="SDV Debug" />
            <FeatureFlagToggle featureFlag={FeatureFlags.ExperimentalFeatures} label="Experimental Features" />
            <FeatureFlagToggle featureFlag={FeatureFlags.GridDebugEnabled} label="Grid Debug" />
            <FeatureFlagToggle featureFlag={FeatureFlags.ExtractionModelSelectionEnabled} label="Extraction Model Selection" />
        </div>
    );
}

